import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Sparkles, Send, Plus, MessageSquare, Trash2, X, Loader2 } from "lucide-react";
import { devWarn } from "@/lib/log";

const QUICK_PROMPTS = [
  "How many pending orders do we have?",
  "What did we spend on fuel this month?",
  "Who has the most overdue tasks?",
  "Show me today's orders",
];

/**
 * RimBot — floating "Ask AI" FAB + full-height side drawer with Claude Sonnet chat.
 * Non-authenticated users see nothing.
 */
export default function AiChatbot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSid, setCurrentSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      try {
        const { data } = await api.get("/ai/chat/sessions");
        setSessions(data || []);
      } catch (e) { devWarn("chat sessions load failed", e); }
    })();
  }, [open, user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  if (!user) return null;
  // Hide the floating RimBot on the Chat page — the chat header has its own
  // dedicated AI button, and stacking two FABs at the same bottom-right
  // position covers the composer's send/mic controls on mobile.
  if (typeof window !== "undefined" &&
      window.location?.pathname?.startsWith("/chat")) {
    return null;
  }

  const startNew = () => { setCurrentSid(null); setMessages([]); setDraft(""); };

  const loadSession = async (sid) => {
    setLoadingHist(true);
    setCurrentSid(sid);
    try {
      const { data } = await api.get(`/ai/chat/history/${sid}`);
      setMessages(data.messages || []);
    } catch (e) { toast.error(formatApiErrorDetail(e, "Failed to load chat")); }
    finally { setLoadingHist(false); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this chat? This cannot be undone.")) return;
    try {
      await api.delete(`/ai/chat/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.session_id !== sid));
      if (currentSid === sid) startNew();
      toast.success("Chat deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e, "Delete failed")); }
  };

  const send = async (text) => {
    const body = (text ?? draft).trim();
    if (!body || sending) return;
    setDraft("");
    setSending(true);
    // Optimistic: append user message immediately
    const nowIso = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", text: body, created_at: nowIso }]);
    try {
      const { data } = await api.post("/ai/chat", { session_id: currentSid, text: body });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, created_at: data.created_at }]);
      if (!currentSid) {
        setCurrentSid(data.session_id);
        // Refresh sessions list so the new one appears
        try {
          const { data: sl } = await api.get("/ai/chat/sessions");
          setSessions(sl || []);
        } catch (e) { devWarn("chat sessions refresh failed", e); }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "AI failed"));
      // roll back optimistic user msg
      setMessages((m) => m.filter((mm) => !(mm.role === "user" && mm.text === body && mm.created_at === nowIso)));
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Floating FAB — hidden on /login and /shop */}
      <button
        type="button"
        data-testid="ai-chatbot-fab"
        onClick={() => setOpen(true)}
        className="fixed z-30 right-4 bottom-20 md:bottom-6 h-14 w-14 rounded-full bg-[#FF3B30] text-white shadow-lg shadow-black/20 hover:bg-[#0D0D0D] active:scale-95 transition-transform flex items-center justify-center"
        aria-label="Ask RimBot"
      >
        <Sparkles className="h-6 w-6" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[520px] p-0 flex flex-col bg-white dark:bg-neutral-950 border-l-2 border-black dark:border-neutral-800"
          data-testid="ai-chatbot-sheet"
        >
          <SheetHeader className="px-4 py-3 border-b-2 border-black dark:border-neutral-800 flex flex-row items-center justify-between gap-2 space-y-0">
            <SheetTitle className="flex items-center gap-2 font-heading text-lg">
              <Sparkles className="h-5 w-5 text-[#FF3B30]" />
              RimBot <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400" data-testid="rimbot-model-badge">/ Claude Sonnet 4.6</span>
            </SheetTitle>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={startNew}
                data-testid="ai-chat-new-btn"
                title="New chat"
                className="h-9 w-9 flex items-center justify-center border border-black dark:border-neutral-700 active:bg-[#FF3B30] active:text-white"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </SheetHeader>

          {/* Sessions strip */}
          {sessions.length > 0 && (
            <div className="border-b border-neutral-200 dark:border-neutral-800 p-2 overflow-x-auto whitespace-nowrap" data-testid="ai-chat-sessions">
              {sessions.map((s) => (
                <div key={s.session_id} className="inline-flex items-center mr-2">
                  <button
                    type="button"
                    onClick={() => loadSession(s.session_id)}
                    data-testid={`ai-chat-session-${s.session_id}`}
                    className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest border px-2 py-1 ${
                      currentSid === s.session_id
                        ? "bg-[#0D0D0D] text-white border-[#0D0D0D] dark:bg-white dark:text-black dark:border-white"
                        : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300"
                    }`}
                  >
                    <MessageSquare className="h-3 w-3" />
                    <span className="max-w-[140px] truncate">{s.title}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSession(s.session_id)}
                    data-testid={`ai-chat-delete-${s.session_id}`}
                    className="h-7 w-7 flex items-center justify-center text-neutral-400 hover:text-[#FF3B30]"
                    aria-label="Delete chat"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ai-chat-messages">
            {loadingHist ? (
              <div className="text-center text-xs font-mono uppercase tracking-widest text-neutral-400 py-8">Loading…</div>
            ) : messages.length === 0 ? (
              <div className="space-y-4">
                <div className="text-sm text-neutral-600 dark:text-neutral-300">
                  Hey <b>{user.name?.split(" ")[0] || "there"}</b> — I can now see your live data. Ask me about pending orders, expenses, overdue tasks, or draft a message.
                </div>
                <div className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Try one</div>
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => send(p)}
                      data-testid={`ai-chat-quick-${p.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                      className="w-full text-left text-sm border border-neutral-200 dark:border-neutral-800 px-3 py-2 hover:border-[#FF3B30] hover:bg-[#FF3B30]/5 dark:hover:bg-[#FF3B30]/10"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={`${m.created_at}-${i}`}
                  data-testid={`ai-chat-msg-${m.role}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed border-2 ${
                      m.role === "user"
                        ? "bg-[#0D0D0D] text-white border-[#0D0D0D] dark:bg-white dark:text-black dark:border-white"
                        : "bg-white text-black border-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="flex justify-start" data-testid="ai-chat-typing">
                <div className="max-w-[85%] px-3 py-2 text-sm border-2 border-neutral-300 dark:border-neutral-700 flex items-center gap-2 text-neutral-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t-2 border-black dark:border-neutral-800 p-3 flex items-center gap-2 bg-white dark:bg-neutral-950"
          >
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Message RimBot…"
              disabled={sending}
              data-testid="ai-chat-input"
              className="flex-1 h-11 px-3 border-2 border-black dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white outline-none focus:border-[#FF3B30] disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              data-testid="ai-chat-send-btn"
              className="h-11 w-11 flex items-center justify-center bg-[#FF3B30] text-white border-2 border-[#FF3B30] active:bg-[#0D0D0D] active:border-[#0D0D0D] disabled:opacity-40"
              aria-label="Send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
